import { AbstractFormElement, ICustomFormElement } from "../../Components/Form/Abstract";
import { getImageUrls } from "./Data";

import { Toast } from "../../Components/Dialog/Toast";

class ImageUploader extends AbstractFormElement implements ICustomFormElement {
    protected declare input: HTMLInputElement;

    declare imageTemplate: HTMLTemplateElement;

    private uploadBox: HTMLSpanElement;
    private imagesBox: HTMLSpanElement;
    private _files: File[] = [];
    private _images: string[] = [];
    private _thumbnails: string[] = [];

    constructor() {
        super();
    }

    initElements() {
        super.initElements();
        this.input.type = "file";
        this.input.multiple = this.multiple;
        this.input.accept = this.accept;
        this.uploadBox = this.querySelector(".upload-box");
        this.imagesBox = this.querySelector(".images");
        this.images = JSON.stringify(this._images);
        this.thumbnails = JSON.stringify(this._thumbnails);
        return this;
    }

    attachEvents() {
        this.uploadBox.addEventListener("click", () => {
            this.input.click();
        });

        this.uploadBox.addEventListener("dragover", this.handleDragAndDrop.bind(this));
        this.uploadBox.addEventListener("drop", this.handleDragAndDrop.bind(this));

        this.input.addEventListener("change", (e) => {
            const files = Array.from((e.target as HTMLInputElement).files);
            if (files.length) {
                this.addFiles(files).then((r) => r);
            }
        });
        return super.initElements();
    }

    private handleDragAndDrop(e: DragEvent): void {
        e.preventDefault();
        if (e.dataTransfer.items) {
            const filesArray = Array.from(e.dataTransfer.files);
            this.addFiles(filesArray).then((r) => r);
        }
        this.uploadBox.classList.toggle("dragover", e.type === "dragover");
    }

    public async addFiles(files: File[]): Promise<void> {
        if (files.length) {
            for (const file of files) {
                if (this.isAcceptedFileType(file) && !this.isFileExists(file) && !this.isFileOk(file)) {
                    try {
                        await getImageUrls(file).then((urls) => {
                            if (urls) {
                                const params = {
                                    file,
                                    thumbnailUrl: urls.thumbnailUrl,
                                    alreadyUploaded: false
                                };
                                this.previewFile(params);
                                this.addImagesToArray(urls.imageUrl, urls.thumbnailUrl);
                                this.addFileToArray(file);
                                this.dispatchEvent(new CustomEvent("change"));
                            }
                        });
                    } catch (e) {
                        this.displayError("uploadError");
                    }
                }
            }
        }
    }

    private removeFile(file: File, e: Event): void {
        const removeButton = e.target as HTMLElement;
        const imageElement = removeButton.closest(".image");
        const images = imageElement.parentElement;
        const index = Array.from(images.children).indexOf(imageElement);

        if (file) {
            this._files = this._files.filter((f) => f !== file);
        }
        this._images = this._images.filter((u, i) => i !== index);
        this._thumbnails = this._thumbnails.filter((u, i) => i !== index);

        this.saveState();

        removeButton.closest(".image").remove();
        removeButton.removeEventListener("click", this.removeFile.bind(this, file));

        this.dispatchEvent(new CustomEvent("change"));
    }

    /**
     * params
     * {
     * file: File,
     * alreadyUploaded: boolean = false,
     * imageUrl: string = null,
     * thumbnailUrl: string = null
     * }
     */
    public async previewFile(params: {
        file?: File;
        alreadyUploaded?: boolean;
        imageUrl?: string;
        thumbnailUrl?: string;
    }): Promise<void> {
        const file = params.file;
        const alreadyUploaded = params.alreadyUploaded || false;
        const imageUrl = params.imageUrl || null;
        const thumbnailUrl = params.thumbnailUrl || null;
        const reader = new FileReader();
        const template = this.imageTemplate.content.cloneNode(true) as DocumentFragment;
        const imageElement = template.querySelector(".image");
        this.imagesBox.appendChild(imageElement);
        new Promise(() => {
            const img = document.createElement("img");
            if (thumbnailUrl) {
                img.src = thumbnailUrl;
            }
            imageElement.appendChild(img);

            const removeButton = imageElement.querySelector(".remove");
            removeButton.addEventListener("click", this.removeFile.bind(this, file));
            if (alreadyUploaded && file) {
                this.addFileToArray(file);
                this.addImagesToArray(imageUrl, thumbnailUrl);
            }
            reader.readAsDataURL(file);
        });
    }

    private addFileToArray(file: File): void {
        this._files = [...this._files, file];
    }

    private addImagesToArray(image: string, thumbnail: string): void {
        this._images = [...this._images, image];
        this._thumbnails = [...this._thumbnails, thumbnail];
        this.saveState();
    }

    private saveState(): void {
        let localStorageData = JSON.parse(localStorage.getItem("purchase-form")) || {};
        localStorageData = {
            ...localStorageData,
            images: this._images,
            thumbnails: this._thumbnails
        };

        localStorage.setItem("purchase-form", JSON.stringify(localStorageData));
    }

    private isFileExists(newFile: File): boolean {
        const isFileExists = this._files.some((oldFile) => {
            return (
                oldFile.name === newFile.name &&
                oldFile.size === newFile.size &&
                oldFile.lastModified === newFile.lastModified
            );
        });
        if (isFileExists) {
            this.displayError("fileExists");
        }
        return isFileExists;
    }

    private isFileOk(file: File): boolean {
        const isFileSizeExceeds = file.size >= this.maxFileSize;
        if (isFileSizeExceeds) {
            this.displayError("fileSize");
        }
        return isFileSizeExceeds;
    }

    private isAcceptedFileType(file: File): boolean {
        const acceptedTypes = this.accept.split(",").map((type) => type.trim().replace(".", "").replace("*", ""));
        const isAcceptedFileType = acceptedTypes.some((type) => file.type.includes(type));

        if (!isAcceptedFileType) {
            this.displayError("fileType");
        }
        return isAcceptedFileType;
    }

    private displayError(title: string): void {
        new Toast({
            title: window.ValidityMessages[this.input.type][title],
            type: Toast.TYPE_ERROR
        })?.render();
    }

    public reset() {
        this._images = [];
        this._thumbnails = [];
        this._files = [];
        this.imagesBox.innerHTML = "";
        this.input.dispatchEvent(new Event("change"));
    }

    get multiple(): boolean {
        return Boolean(JSON.parse(this.getAttribute("multiple")));
    }

    get accept(): string {
        return this.getAttribute("accept") || ".jpg, .jpeg, .png";
    }

    get maxFileSize(): number {
        return 1024 * 1024 * (Number(this.dataset.maxFileSize) || 20);
    }

    set value(value: string) {
        this.input.value = "";
    }

    set images(value: NonNullable<unknown>) {
        if (typeof value === "string") {
            this._images = JSON.parse(value);
        }
    }

    get images(): string {
        return JSON.stringify(this._images);
    }

    set thumbnails(value: NonNullable<unknown>) {
        if (typeof value === "string") {
            this._thumbnails = JSON.parse(value);
        }
    }

    get thumbnails(): string {
        return JSON.stringify(this._thumbnails);
    }

    set files(v: File[]) {
        this._files = v;
    }

    get files(): File[] {
        return this._files;
    }
}

ImageUploader.prototype.imageTemplate = document.createElement("template");
ImageUploader.prototype.imageTemplate.innerHTML = `
    <span class="image">
    <span class="icon trash remove"></span>
    </span>
`;

ImageUploader.prototype.template = document.createElement("template");

ImageUploader.prototype.template.innerHTML = /*html*/ `
<span class="upload-box">
    <span class="icon upload"></span>
</span>
<label class="std-fe">
    <span class="label"></span>
    <span class="input"></span>
</label>
<span class="images"></span>
<span class="tags"></span>
<span class="error"></span>
<span class="help"></span>
`;

customElements.define("x-image-uploader", ImageUploader);

export { ImageUploader };
